import React from "react";

import { isNotNullOrUndefined } from "../../../../../../Common/TypingUtils";
import { BindingPath } from "../../../../Engine/Core/CoreTypes";
import { getFilterKey } from "../../../../Helpers/FilteringHelpers";
import { PathResolver, withResolvePaths } from "../../../withResolvePaths";
import { SortType } from "../../MultiLine/MultiLineTypes";
import { AbsoluteModelPath, isModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { useMultilineClient } from "../../../../Engine/Core/Filtering/UseMultilineClient";
import { createHocDisplayName } from "../../../TypingUtils";

import { HeaderMenu, HeaderMenuProps } from "./HeaderMenu";

export interface HeaderMenuOwnProps extends HeaderMenuProps {
    path: AbsoluteModelPath;
    type: string;
    children: React.ReactNode;
    header: string | JSX.Element;
    inputPlaceholder: string;
    resolvePath: PathResolver;
    filteredUniqKey?: string;
}

interface HeaderMenuMappedProps {
    filtered: boolean;
    sorted: SortType | undefined;
}

const HeaderMenuContainerWrapper: React.FC<HeaderMenuOwnProps> = ownProps => {
    const { path, type, children, header, inputPlaceholder, resolvePath, filteredUniqKey = "default" } = ownProps;
    const resolvedMultilinePath = resolvePath(path);
    const { filters, multilineSort } = useMultilineClient(resolvedMultilinePath, filteredUniqKey, info => ({
        filters: info.multilineState.filters,
        multilineSort: info.multilineState.sorting,
    }));

    const paths = React.Children.map(children ?? [], (child): BindingPath | undefined => {
        if (!React.isValidElement<{ dataPath?: unknown; dataPaths?: unknown }>(child)) {
            return undefined;
        }
        if (typeof child.props.dataPath === "string") {
            return child.props.dataPath;
        }
        if (isModelPath(child.props.dataPath)) {
            return child.props.dataPath.toLegacyPath();
        }
        if (Array.isArray(child.props.dataPaths)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            return getFilterKey(child.props.dataPaths);
        }
        return undefined;
    })
        .filter(isNotNullOrUndefined)
        .map((x: BindingPath): BindingPath => resolvePath(x))
        .filter(isNotNullOrUndefined);

    const filtered = filters && paths.some(p => filters.some(x => x.filterSourceId === p));
    const sorted = multilineSort != undefined ? paths.some(x => x === multilineSort.dataPath) : false;

    const mappedProps: HeaderMenuMappedProps = {
        filtered: Boolean(filtered),
        sorted: multilineSort != undefined && sorted ? multilineSort.value : undefined,
    };

    return (
        <HeaderMenu
            {...ownProps}
            path={path}
            type={type}
            header={header}
            inputPlaceholder={inputPlaceholder}
            {...mappedProps}>
            {children}
        </HeaderMenu>
    );
};

HeaderMenuContainerWrapper.displayName = createHocDisplayName(HeaderMenu, "HeaderMenuContainerWrapper");

export const HeaderMenuContainer = withResolvePaths(HeaderMenuContainerWrapper);
